import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { PatternProduct } from '@/shared/composables/patternProducts/patternProducts.types';

export const usePatternProductsStore = defineStore('pattern-products', () => {
  /**
   * Core state - the source of truth for pattern products
   * Pattern products for the currently selected size.
   * Contains ALL products (including hidden/deleted) with user modifications.
   * Gets completely replaced on size changes but preserves user changes where possible.
   */
  const allPatternProductsForSize = ref<PatternProduct[]>([]);
  const currentSize = ref<string>('');

  const isValidPatternProduct = (pp: Partial<PatternProduct>) =>
    !!(pp.product_id && pp.variant_id);

  const hasRequiredData = (pp: PatternProduct) =>
    pp.quantity > 0 && isValidPatternProduct(pp);

  // Core computed properties
  const activePatternProducts = computed<PatternProduct[]>(() =>
    allPatternProductsForSize.value.filter(hasRequiredData)
  );

  // Initialize store with data
  const initialize = (
    initialPatternProducts: PatternProduct[],
    size?: string
  ) => {
    allPatternProductsForSize.value = [...initialPatternProducts];
    if (size) currentSize.value = size;
  };

  const handleSizeChange = (size: string, newSizeData: PatternProduct[]) => {
    if (!newSizeData) return;

    const newPatternProducts: PatternProduct[] = [];
    const deletedPatternProducts: PatternProduct[] = [];
    const matchedProducts = new Set<PatternProduct>();

    // Handle new size data
    newSizeData.forEach((newPP) => {
      if (!isValidPatternProduct(newPP)) return;

      const existingProduct = allPatternProductsForSize.value.find(
        (currentPP) =>
          currentPP.product_id === newPP.product_id &&
          currentPP.variant_id === newPP.variant_id
      );

      if (existingProduct) {
        newPatternProducts.push({
          ...existingProduct,
          quantity: newPP.quantity ?? 1
        });
        matchedProducts.add(existingProduct);
      } else {
        newPatternProducts.push(newPP);
      }
    });

    // Create map for efficient lookup
    const newPatternProductsMap = new Map(
      newPatternProducts.map((pp) => [
        `${pp.product_id}-${pp.variant_id}`,
        pp.quantity
      ])
    );

    // Handle products that are no longer in the new size
    allPatternProductsForSize.value.forEach((currentPP) => {
      const key = `${currentPP.product_id}-${currentPP.variant_id}`;
      const isPresentInNew = newPatternProductsMap.has(key);

      if (!isPresentInNew && !matchedProducts.has(currentPP)) {
        deletedPatternProducts.push({
          ...currentPP,
          quantity: 0 // Mark as deleted but preserve for reference
        });
      }
    });

    // Update state
    allPatternProductsForSize.value = [
      ...newPatternProducts,
      ...deletedPatternProducts
    ];
    currentSize.value = size;
  };

  // Update quantity by index in the source array
  const updateQuantityBySourceIndex = (
    sourceIndex: number,
    quantity: number
  ) => {
    if (allPatternProductsForSize.value[sourceIndex]) {
      allPatternProductsForSize.value[sourceIndex] = {
        ...allPatternProductsForSize.value[sourceIndex],
        quantity
      };
    }
  };

  // Update variant by index in the source array
  const updateVariantBySourceIndex = (
    sourceIndex: number,
    productId: string,
    variantId: string
  ) => {
    const patternProduct = allPatternProductsForSize.value[sourceIndex];
    if (patternProduct && patternProduct.product_id === productId) {
      allPatternProductsForSize.value[sourceIndex] = {
        ...allPatternProductsForSize.value[sourceIndex],
        variant_id: variantId
      };
    }
  };

  return {
    // State
    allPatternProductsForSize,
    currentSize,

    // Computed
    activePatternProducts,

    // Actions
    initialize,
    handleSizeChange,
    updateQuantityBySourceIndex,
    updateVariantBySourceIndex,

    // Utilities (for composable use)
    hasRequiredData
  };
});
